import { withTheme } from "@emotion/react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Fragment, useEffect, useState } from "react"

import { authClient, marketingClient, useMutation } from "@trueskin-web/apis"
import {
  ALink,
  Button,
  Dialog,
  FieldError,
  Heading,
} from "@trueskin-web/components"
import { getErrorMessage } from "@trueskin-web/functions"
import { Trans } from "@trueskin-web/translations"

const ExistingAccountDialog = withTheme(({ email, image, onDismiss }) => {
  const {
    mutateAsync: requestLoginEmail,
    status,
    error,
  } = useMutation(authClient.requestLoginEmail)

  const [loginEmailRequested, setLoginEmailRequested] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const countdownInMs = 30000
  let requestLoginEmailTimer

  useEffect(() => {
    marketingClient.sendEvent({
      name: marketingClient.EVENT_TYPES_ENUM.LOGIN_POPUP_OPENED,
    })
  }, [])

  useEffect(() => {
    requestLoginEmail({ email })

    return () => clearTimeout(requestLoginEmailTimer)
  }, [email, requestLoginEmail, requestLoginEmailTimer])

  const handleClick = async () => {
    setCountdown(countdownInMs / 1000)

    await requestLoginEmail({ email })

    setLoginEmailRequested(true)

    const countdownTimer = setInterval(() => {
      setCountdown((prevCountdown) => prevCountdown - 1)
    }, 1000)

    requestLoginEmailTimer = setTimeout(() => {
      clearInterval(countdownTimer)
    }, countdownInMs)
  }

  const isButtonDisabled = status !== "success" || countdown > 0

  return (
    <Dialog
      dataTestId="web-portal-create-account-existing-user-dialog"
      aria-label="Existing Account"
      onDismiss={onDismiss}
      sx={{
        textAlign: "center",
      }}
    >
      <GatsbyImage image={getImage(image)} alt="" width={162} height={120} />
      <Heading mt={5} sx={{ variant: "vwo.heading" }}>
        {loginEmailRequested ? (
          <Trans i18nKey="Start.CreateAccount.ExistingAccountDialog.emailHasBeenSentHeading" />
        ) : (
          <Trans i18nKey="Start.CreateAccount.ExistingAccountDialog.existingAccountHeading" />
        )}
      </Heading>
      <div sx={{ mt: 2 }}>
        <Trans
          i18nKey={
            loginEmailRequested
              ? "Start.CreateAccount.ExistingAccountDialog.emailHasBeenSentMessage"
              : "Start.CreateAccount.ExistingAccountDialog.existingAccountMessage"
          }
          values={{ email }}
          components={{
            action: (
              <ALink
                href={`mailto:${email}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  variant: "vwo.heading",
                  "&, &:hover, &:focus": {
                    color: "darkBlue",
                  },
                }}
              />
            ),
          }}
        />
      </div>
      <Button
        variant="link"
        size="xsm"
        disabled={isButtonDisabled}
        onClick={handleClick}
        sx={{
          mt: 5,
          color: "primary",
          textTransform: "uppercase",
          fontWeight: "bold",
          letterSpacing: "0.22em",
          "&:disabled": {
            color: "lightGreen",
          },
          variant: "vwo.button.link",
        }}
      >
        {loginEmailRequested ? (
          <Fragment>
            <Trans i18nKey="Start.CreateAccount.ExistingAccountDialog.resendEmailButton" />{" "}
            {countdown > 0 && `(${countdown})`}
          </Fragment>
        ) : (
          <Trans i18nKey="Start.CreateAccount.ExistingAccountDialog.emailNotReceivedButtonLabel" />
        )}
      </Button>
      {error ? <FieldError>{getErrorMessage(error)}</FieldError> : null}
    </Dialog>
  )
})

export default ExistingAccountDialog
